import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "./DashboardLayout";
import "./dashboard-overview.css";
import { formatOverviewDate, formatRupees } from "./overviewFormatting";
import {
  DonationRow,
  MemberRow,
  VolunteerRow,
  fetchDonations,
  fetchMembers,
  fetchTestimonialsCount,
  fetchVolunteers,
} from "./api";

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");

const normalizeSkills = (raw?: string) => {
  if (!raw) return "-";
  const value = String(raw).trim();
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      const normalized = parsed.map((item) => String(item).trim()).filter(Boolean).join(", ");
      return normalized || "-";
    }
  } catch {
    // fall through to text normalization
  }

  const cleaned = value
    .replace(/^\[/, "")
    .replace(/\]$/, "")
    .replace(/"/g, "")
    .replace(/'/g, "")
    .trim();
  return cleaned || "-";
};

const DashboardHome: React.FC = () => {
  const [memberRows, setMemberRows] = useState<MemberRow[]>([]);
  const [donationRows, setDonationRows] = useState<DonationRow[]>([]);
  const [volunteerRows, setVolunteerRows] = useState<VolunteerRow[]>([]);
  const [volunteerCount, setVolunteerCount] = useState(0);
  const [testimonialCount, setTestimonialCount] = useState(0);
  const [donationSum, setDonationSum] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const [m, v, d, t] = await Promise.all([
          fetchMembers(),
          fetchVolunteers(),
          fetchDonations(),
          fetchTestimonialsCount(),
        ]);
        setMemberRows(m);
        setVolunteerCount(v.length);
        setVolunteerRows(v);
        setDonationRows(d);
        setTestimonialCount(t);
        setDonationSum(d.reduce((acc, item) => acc + Number(item.amount || 0), 0));
      } catch (err: any) {
        setError(err.message || "Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [attempt]);

  const donationsText = useMemo(() => formatRupees(donationSum), [donationSum]);
  const recentMembers = useMemo(() => memberRows.slice(0, 3), [memberRows]);
  const recentDonations = useMemo(() => donationRows.slice(0, 3), [donationRows]);
  const recentVolunteers = useMemo(() => volunteerRows.slice(0, 3), [volunteerRows]);

  const statCards = [
    { key: "members", label: "Members", value: memberRows.length, icon: "fa-solid fa-users", iconClass: "icon-members", href: "/dashboard/members" },
    { key: "volunteers", label: "Volunteers", value: volunteerCount, icon: "fa-solid fa-hand-holding-heart", iconClass: "icon-volunteers", href: "/dashboard/volunteers" },
    { key: "donations", label: "Donations", value: donationsText, icon: "fa-regular fa-heart", iconClass: "icon-donations", href: "/dashboard/donations" },
    { key: "testimonials", label: "Testimonials", value: testimonialCount, icon: "fa-regular fa-comment-dots", iconClass: "icon-testimonials", href: "/dashboard/testimonials" },
  ];

  return (
    <DashboardLayout>
      <div className="dashboard-overview">
      <h1 className="overview-title">Overview</h1>
      <p className="overview-subtitle">A snapshot of your organization.</p>
      {loading && <p role="status">Loading overview...</p>}
      {!loading && error && <div className="overview-error" role="alert">Unable to load overview: {error} <button type="button" onClick={() => setAttempt((value) => value + 1)}>Retry</button></div>}

      {!loading && !error && <>
      <div className="stat-grid">
        {statCards.map((card) =>
          card.href ? (
            <Link to={card.href} key={card.key} className="stat-card stat-card-link stat-primary">
              <div className={`stat-icon ${card.iconClass}`}>
                <i className={card.icon} />
              </div>
              <div>
                <div className="stat-label">{card.label}</div>
                <div className="stat-value">{card.value}</div>
              </div>
            </Link>
          ) : (
            <div className="stat-card" key={card.key}>
              <div className={`stat-icon ${card.iconClass}`}>
                <i className={card.icon} />
              </div>
              <div>
                <div className="stat-label">{card.label}</div>
                <div className="stat-value">{card.value}</div>
              </div>
            </div>
          )
        )}
      </div>

      <div className="recent-grid">
        <section>
          <h2 className="recent-title">Recent Members</h2>
          {recentMembers.map((member) => (
            <div className="recent-item" key={member.id}>
              <div className="recent-user">
                <span className="avatar-dot">{getInitials(member.fullName)}</span>
                <div>
                  <div className="recent-name">{member.fullName}</div>
                  <div className="recent-sub">Joined {formatOverviewDate(member.createdAt)}</div>
                </div>
              </div>
              <span className="recent-badge">{member.city || "-"}</span>
            </div>
          ))}
          {recentMembers.length === 0 && <div className="recent-sub">No members found</div>}
        </section>

        <section>
          <h2 className="recent-title">Recent Donations</h2>
          {recentDonations.map((donation) => (
            <div className="recent-item" key={donation.id}>
              <div className="recent-user">
                <span className="avatar-dot">{getInitials(donation.fullName)}</span>
                <div>
                  <div className="recent-name">{donation.fullName}</div>
                  <div className="recent-sub">
                    {donation.paymentMethod || "-"} - {formatOverviewDate(donation.createdAt)}
                  </div>
                </div>
              </div>
              <span className="recent-badge">{formatRupees(Number(donation.amount || 0))}</span>
            </div>
          ))}
          {recentDonations.length === 0 && <div className="recent-sub">No donations found</div>}
        </section>

        <section>
          <h2 className="recent-title">Recent Volunteers</h2>
          {recentVolunteers.map((volunteer) => (
            <div className="recent-item" key={volunteer.id}>
              <div className="recent-user">
                <span className="avatar-dot">{getInitials(volunteer.fullName)}</span>
                <div>
                  <div className="recent-name">{volunteer.fullName}</div>
                  <div className="recent-sub">{normalizeSkills(volunteer.skills)}</div>
                </div>
              </div>
              <span className="recent-badge">{volunteer.availability || "-"}</span>
            </div>
          ))}
          {recentVolunteers.length === 0 && <div className="recent-sub">No volunteers found</div>}
        </section>

      </div>
      </>}
      </div>
    </DashboardLayout>
  );
};

export default DashboardHome;
